import {useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {Alert, SimpleGrid} from '@mantine/core';
import {I18n} from '@aws-amplify/core';
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {DataTableTokenPagination, MantineDataTable, TableSearchForm} from '@flexinet/ui-components';

import {useUsers} from '../hooks/useUsers';
import {useUsersSearch} from '../hooks/useUsersSearch';
import {useUsersSelection} from '../hooks/useUsersSelection';
import {rbacActions} from '../../../config/rbac/actions';
import {User} from '../types';
import config from '../config';

// The users list on the shared MantineDataTable (checklist §3z), paged with
// users-api's nextToken and the shared DataTableTokenPagination (§9). The
// console's copy hand-rolled a Mantine <Table> on a single unpaged query: no
// sorting, no error state, one empty state, and a page-size control.
export const RecordsTable = () => {
    const navigate = useNavigate();
    const {allowedActions} = useVerifiedPermissions();

    const {
        columns,
        // Everything loaded, before keyword and status narrow it: the footer's
        // "N of M loaded" is measured against this.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        nextToken,
        isFetchingMore,
        fetchRecords,
    } = useUsers();

    const {keyword, handleFilterByKeyword} = useUsersSearch();
    const {selection, handleRowSelection, handleClearSelection} = useUsersSelection();

    // Checkboxes only for someone who can act on a selection: delete.
    const canDelete = allowedActions.includes(rbacActions.DeleteUser);
    const selectedRecords = canDelete
        ? visibleItems.filter(record => selection[record.id] === true)
        : undefined;

    const scrollViewportRef = useRef<HTMLDivElement>(null);
    const searching = keyword.trim() !== '';

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load users')}>
                    {I18n.get('users-api did not return the list. Try Reload; if it keeps failing, the API may be unavailable on this stage.')}
                </Alert>
            )}

            <MantineDataTable<User>
                columns={columns}
                records={visibleItems}
                // Client-side over the pages loaded so far, like the keyword filter.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={canDelete ? (records) => {
                    handleClearSelection();
                    (records as User[]).forEach(record => handleRowSelection(record.id, true));
                } : undefined}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) => navigate(`${config.basePath}/${encodeURIComponent(record.id)}`)}
            />

            {/*
              TWO empty states, not one (§3, §13). "Nobody here" and "your search
              matched nobody" mean different things. Held back while loading so it
              does not flash, and while in error so a failed load is not reported
              as an empty directory.
            */}
            {!loading && !isError && visibleItems.length === 0 && (
                <Alert color={searching ? 'gray' : 'blue'}
                       title={searching ? I18n.get('No matching users') : I18n.get('No users yet')}>
                    {searching
                        ? `${I18n.get('Nobody loaded so far matches')} "${keyword.trim()}". ${I18n.get('A match may still be on a page that has not loaded yet.')}`
                        : I18n.get('Nobody has been added to this directory yet.')}
                </Alert>
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'user'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
